import { Button, Dialog, DialogActions, DialogTitle } from '@mui/material';
import { useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { useService } from 'shared/providers';
import { useContractInventoryFormState } from 'shared/contexts/contract-inventory-form-context';
import { useGetContract } from 'shared/queries';
import { ContractDocument } from 'shared/types';
import {
  useUpdateContract,
  useCreateDraftContract,
  useUpdateDraftContract,
  useSendContractNotification,
} from 'shared/mutations';
import { useAuthState } from 'shared/stores';

export function ContractDeleteDialog() {
  const { appConfig } = useService();
  const { enqueueSnackbar } = useSnackbar();
  const updateContractMutation = useUpdateContract();
  const createDraftContractMutation = useCreateDraftContract();
  const updateDraftContractMutation = useUpdateDraftContract();
  const notificationMutation = useSendContractNotification();
  const authState = useAuthState();
  const {
    contractIdToDelete,
    setContractIdToDelete,
    contractDeleteDialogOpen,
    toggleContractDeleteDialog,
  } = useContractInventoryFormState();
  const { contract } = useGetContract(contractIdToDelete);
  const environment = appConfig.environment;
  const user = authState.user.value?.displayName || '';

  const onClose = useCallback(() => {
    setContractIdToDelete('');
    toggleContractDeleteDialog();
  }, [setContractIdToDelete, toggleContractDeleteDialog]);

  const onDelete = useCallback(async () => {
    const shouldAskApproval = !!contract?.approvedDate;
    const isContractDeleted = contract?.deletedAt;

    const updatedContract: ContractDocument = {
      ...contract!,
      status: shouldAskApproval ? 'pending' : 'active',
      deletedAt: isContractDeleted ? '' : new Date().toISOString(),
      deletedBy: isContractDeleted ? '' : user,
      markedForDeletionOrRestorationOn: new Date().toISOString(),
    };

    if (contract && !shouldAskApproval) {
      await updateContractMutation.mutateAsync(updatedContract);
    }

    if (shouldAskApproval) {
      if (contract?.status === 'rejected') {
        await updateDraftContractMutation.mutateAsync(updatedContract);
      } else {
        await createDraftContractMutation.mutateAsync(updatedContract);
      }
      await notificationMutation.mutateAsync({
        contract: updatedContract,
        template: 'delete',
        comments: '',
        environment,
      });
      enqueueSnackbar('Approval requested!', { variant: 'success' });
    }
    toggleContractDeleteDialog();
    setContractIdToDelete('');
  }, [
    contract,
    user,
    environment,
    updateContractMutation,
    notificationMutation,
    enqueueSnackbar,
    toggleContractDeleteDialog,
    setContractIdToDelete,
    createDraftContractMutation,
    updateDraftContractMutation,
  ]);

  return (
    <Dialog open={contractDeleteDialogOpen} onClose={onClose} fullWidth={true} aria-labelledby='delete-dialog'>
      <DialogTitle id='delete-dialog-title'>{`Are you sure you want to ${
        contract?.deletedAt ? 'restore' : 'delete'
      } this contract?`}</DialogTitle>
      <DialogActions>
        <Button onClick={onClose} color='primary'>
          No
        </Button>
        <Button onClick={onDelete} color='primary'>
          Yes
        </Button>
      </DialogActions>
    </Dialog>
  );
}
